import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/context/AuthContext';
import { formatarCpf, validarCpf } from '@/lib/cpf';

import {
  AlertaErro,
  AuthShell,
  BotaoPrimario,
  CampoTexto,
  LinkAuth,
} from './AuthShell';

const SENHA_MINIMA = 8;

export default function CadastroPage() {
  const [form, setForm] = useState({
    nome: '',
    email: '',
    cpf: '',
    telefone: '',
    senha: '',
    confirmacao: '',
  });
  const [erros, setErros] = useState({});
  const [erroGeral, setErroGeral] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [concluido, setConcluido] = useState(null);

  const { cadastrar } = useAuth();
  const navigate = useNavigate();

  const atualizar = (campo) => (e) => {
    const valor =
      campo === 'cpf' ? formatarCpf(e.target.value) : e.target.value;
    setForm((atual) => ({ ...atual, [campo]: valor }));
    setErros((atual) => ({ ...atual, [campo]: undefined }));
  };

  const validar = () => {
    const novos = {};

    if (form.nome.trim().length < 2) novos.nome = 'Informe seu nome completo.';
    if (!form.email.includes('@')) novos.email = 'E-mail inválido.';
    if (!validarCpf(form.cpf)) novos.cpf = 'CPF inválido.';
    if (form.senha.length < SENHA_MINIMA)
      novos.senha = `Use pelo menos ${SENHA_MINIMA} caracteres.`;
    if (form.senha !== form.confirmacao)
      novos.confirmacao = 'As senhas não conferem.';

    setErros(novos);
    return Object.keys(novos).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErroGeral('');
    if (!validar()) return;

    setCarregando(true);
    try {
      const resposta = await cadastrar({
        nome: form.nome.trim(),
        email: form.email.trim(),
        cpf: form.cpf,
        senha: form.senha,
        telefone: form.telefone.trim(),
      });
      setConcluido({ ativada: Boolean(resposta?.data?.activated) });
    } catch (err) {
      const codigo = err?.response?.data?.error;

      if (codigo === 'CPF_MISMATCH') {
        setErros((atual) => ({
          ...atual,
          cpf: 'Este CPF não corresponde ao das compras feitas com este e-mail.',
        }));
      } else if (codigo === 'ACCOUNT_ALREADY_EXISTS') {
        setErroGeral(
          'Você já tem uma conta com esses dados. Faça login ou recupere sua senha.',
        );
      } else if (codigo === 'CONFLICTING_ACCOUNTS') {
        setErroGeral(
          'Este e-mail e este CPF já pertencem a contas diferentes. Fale com a organização para unificá-las.',
        );
      } else {
        setErroGeral('Não foi possível criar a conta agora. Tente novamente.');
      }
    } finally {
      setCarregando(false);
    }
  };

  if (concluido) {
    return (
      <AuthShell
        titulo={concluido.ativada ? 'Conta ativada' : 'Conta criada'}
        subtitulo={
          concluido.ativada
            ? 'Seu histórico foi mantido'
            : 'Já pode entrar com sua senha'
        }
      >
        <div className="space-y-5 text-center">
          <p className="text-ink-soft text-sm leading-relaxed">
            {concluido.ativada ? (
              <>
                Encontramos compras anteriores feitas com{' '}
                <strong className="text-ink">{form.email.trim()}</strong>. Suas
                inscrições e pedidos continuam na sua conta.
              </>
            ) : (
              <>
                Sua conta com o e-mail{' '}
                <strong className="text-ink">{form.email.trim()}</strong> está
                pronta.
              </>
            )}
          </p>
          <BotaoPrimario type="button" onClick={() => navigate('/login')}>
            Entrar agora
          </BotaoPrimario>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      titulo="Criar conta"
      subtitulo="Junte-se ao clube de leitura"
      rodape={
        <>
          Já tem conta? <LinkAuth to="/login">Entrar</LinkAuth>
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        <p className="border-petal bg-blush-light text-ink-soft rounded-xl border px-4 py-3 text-xs leading-relaxed">
          Já participou de algum evento? Use o mesmo e-mail e CPF da inscrição —
          sua conta é ativada com o histórico preservado.
        </p>

        <CampoTexto
          rotulo="Nome completo"
          name="nome"
          autoComplete="name"
          autoFocus
          required
          placeholder="Maria Silva"
          value={form.nome}
          onChange={atualizar('nome')}
          erro={erros.nome}
        />

        <CampoTexto
          rotulo="E-mail"
          name="email"
          type="email"
          autoComplete="email"
          required
          placeholder="voce@email.com"
          value={form.email}
          onChange={atualizar('email')}
          erro={erros.email}
        />

        <CampoTexto
          rotulo="CPF"
          name="cpf"
          inputMode="numeric"
          required
          placeholder="000.000.000-00"
          value={form.cpf}
          onChange={atualizar('cpf')}
          erro={erros.cpf}
        />

        <CampoTexto
          rotulo="Telefone (opcional)"
          name="telefone"
          type="tel"
          autoComplete="tel"
          placeholder="(92) 90000-0000"
          value={form.telefone}
          onChange={atualizar('telefone')}
        />

        <CampoTexto
          rotulo="Senha"
          name="senha"
          type="password"
          autoComplete="new-password"
          required
          placeholder="••••••••"
          value={form.senha}
          onChange={atualizar('senha')}
          erro={erros.senha}
          dica={`Mínimo de ${SENHA_MINIMA} caracteres.`}
        />

        <CampoTexto
          rotulo="Confirmar senha"
          name="confirmacao"
          type="password"
          autoComplete="new-password"
          required
          placeholder="••••••••"
          value={form.confirmacao}
          onChange={atualizar('confirmacao')}
          erro={erros.confirmacao}
        />

        <AlertaErro>{erroGeral}</AlertaErro>

        <BotaoPrimario type="submit" carregando={carregando}>
          Criar conta
        </BotaoPrimario>
      </form>
    </AuthShell>
  );
}
